<template>
  <div
    class="flex flex-col md:flex-row gap-4"
    :class="stacked ? 'md:flex-col' : 'md:gap-6'"
  >
    <div v-if="!stacked" class="order-last md:order-none">
      <label class="mb-1 inline-flex text-label text-stone-800 md:hidden">
        {{ t('personalization.steps.amount.quantityLabel') }}
      </label>
      <NumberControlButtons
        v-model="quantity"
        class="giftcard-quantity"
        data-test-id="giftcard-quantity"
        size="sm"
        :min="MIN_QUANTITY"
        :max="MAX_QUANTITY"
        :disable-minus="quantity <= MIN_QUANTITY"
        :disable-plus="quantity >= MAX_QUANTITY"
      />
    </div>
    <div class="min-w-0 flex-1">
      <slot name="presets-label" />
      <div
        ref="chipGroup"
        data-test-id="gifcard-amount-options"
        role="radiogroup"
        :aria-label="presetsLabel || undefined"
        class="giftcard-amount-picker__options flex justify-between gap-4"
        :class="!stacked && 'md:gap-2'"
      >
        <div
          v-for="(amount, index) in presets"
          :key="amount"
          role="radio"
          :aria-checked="isChipSelected(amount)"
          :tabindex="index === focusableChipIndex ? 0 : -1"
          class="chip-amount text-sm font-medium h-[48px] min-h-11 max-w-[120px] py-2 flex-auto text-center justify-center flex items-center cursor-pointer ease duration-300 rounded-3xl border bg-white hover:border-teal-900 hover:text-teal-900 hover:bg-teal-50 text-stone-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-900"
          :class="[chipClass(amount), !stacked && 'md:h-10 md:max-w-none']"
          @click="selectAmount(amount)"
          @keydown="handleChipKeydown($event, index)"
        >
          {{ formatMoney(amount, locale, undefined, 0) }}
        </div>
      </div>
    </div>

    <div class="min-w-0 flex-1" :class="!stacked && 'md:w-48 md:flex-none'">
      <slot name="input-label" />
      <div class="flex items-center gap-2">
        <div class="relative min-w-0 flex-1">
          <div class="relative">
            <input
              :value="display"
              type="text"
              inputmode="numeric"
              data-test-id="giftcard-amount-input"
              class="font-regular w-full h-[42px] px-4 border ease duration-300 border-secondary outline-none focus:border-primary-light rounded-lg"
              :class="[
                error.visible &&
                  (isHero ? '!border-red-700' : 'border-red-500'),
                isHero && 'pr-9',
              ]"
              :placeholder="placeholder"
              :maxlength="isHero ? HERO_MAX_DIGITS : undefined"
              :aria-invalid="error.visible || undefined"
              :aria-describedby="describedBy"
              @input="handleInput"
              @focus="handleFocus"
              @blur="handleBlur"
            />
            <span
              v-if="isHero"
              aria-hidden="true"
              class="pointer-events-none absolute inset-y-0 right-4 flex items-center text-stone-700"
            >
              €
            </span>
          </div>
          <div
            v-if="helperText && !error.visible"
            :id="helperId"
            data-test-id="giftcard-amount-helper"
            class="mt-2 text-sm text-stone-700"
          >
            {{ helperText }}
          </div>
          <div
            :id="errorId"
            aria-live="polite"
            :data-form-error="error.visible || undefined"
            data-test-id="giftcard-amount-error"
            class="text-sm"
            :class="[
              error.visible && 'mt-2',
              isHero ? 'text-red-700' : 'text-danger-active',
            ]"
          >
            <template v-if="error.visible">{{ error.message }}</template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { parsePriceInput, parsePriceToNumber } from '~/utils/priceInputHelpers'

const { t, locale } = useI18n()

type AmountPickerVariant = 'default' | 'hero'

const {
  modelValue,
  presets,
  min = 15,
  max = 200,
  placeholder = '',
  stacked = false,
  required = false,
  variant = 'default',
  presetsLabel = '',
  helperText = '',
  rangeErrorMessage = '',
} = defineProps<{
  /** null = nothing chosen yet (no chip highlighted, empty custom field) */
  modelValue: number | null
  presets: number[]
  min?: number
  max?: number
  placeholder?: string
  /** Stack presets above the custom-amount input instead of placing them side by side - for narrow containers (e.g. the sidebar buy box). */
  stacked?: boolean
  /** Treat "no chip and empty custom field" as an error in checkError() instead of "no override". */
  required?: boolean
  /**
   * `hero` (giftcard page hero): chips and custom field are mutually
   * exclusive, the field takes up to 3 digits with an inline € suffix and is
   * validated on every keystroke, and the selected chip gets a dark fill.
   */
  variant?: AmountPickerVariant
  /** Accessible name for the chip radiogroup. */
  presetsLabel?: string
  /** Shown under the custom field and linked via aria-describedby; replaced by the error while invalid. */
  helperText?: string
  /** Overrides the default out-of-range error message. */
  rangeErrorMessage?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
}>()

const quantity = defineModel<number>('quantity', { default: 1 })
/** true while the custom field holds an out-of-range amount (hero variant only) */
const invalid = defineModel<boolean>('invalid', { default: false })
const MIN_QUANTITY = 1
const MAX_QUANTITY = 1000
const HERO_MAX_DIGITS = 3

const isHero = computed(() => variant === 'hero')

const helperId = useId()
const errorId = useId()
const describedBy = computed(() => {
  if (error.value.visible) return errorId
  return helperText ? helperId : undefined
})

// null = custom field shows the placeholder (either untouched, or modelValue
// currently matches a preset chip so there's nothing distinct to show here)
const inputValue = ref<number | null>(null)
const isFocused = ref(false)
const rawInput = ref('')
const error = ref({
  visible: false,
  message: '',
})

const syncRawInput = () => {
  rawInput.value = inputValue.value === null ? '' : String(inputValue.value)
}

watch(
  () => modelValue,
  (value) => {
    if (isHero.value) {
      // our own emit while typing - the field already shows it
      if (value !== null && value === inputValue.value) return
      // a preset value from outside (initial default, ?amount= prefill)
      // selects its chip and leaves the field empty
      inputValue.value =
        value !== null && presets.includes(value) ? null : value
      syncRawInput()
      return
    }
    inputValue.value = value
    if (!isFocused.value) syncRawInput()
  },
  { immediate: true }
)

// while focused: raw digits, easy to edit. while blurred: "<value> €", matching
// the same pattern as FilterItemPriceRange's price inputs. The hero variant
// renders the € as a fixed suffix instead, so it always shows raw digits.
const display = computed(() => {
  if (isFocused.value || isHero.value) return rawInput.value
  return inputValue.value === null ? '' : `${inputValue.value} €`
})

const isInRange = (value: number) => value >= min && value <= max

const rangeError = () => rangeErrorMessage || t('giftcard.inputError')

const setValue = (value: string) => {
  error.value.visible = false

  if (value === '') {
    rawInput.value = ''
    inputValue.value = null
    invalid.value = false
    // an emptied field leaves nothing chosen rather than silently keeping
    // the last valid amount typed on the way
    if (isHero.value) emit('update:modelValue', null)
    return
  }

  const cleaned = isHero.value
    ? value.replace(/\D/g, '').slice(0, HERO_MAX_DIGITS)
    : parsePriceInput(value)
  rawInput.value = cleaned

  // hero: only non-digits were typed - same as an empty field
  if (isHero.value && cleaned === '') {
    inputValue.value = null
    invalid.value = false
    emit('update:modelValue', null)
    return
  }

  const numeric = parsePriceToNumber(cleaned)
  inputValue.value = numeric

  if (isHero.value) {
    // read back from a local: a parent-bound defineModel keeps returning the
    // old prop value until the parent re-renders
    const isOutOfRange = !isInRange(numeric)
    invalid.value = isOutOfRange
    if (isOutOfRange) {
      error.value.visible = true
      error.value.message = rangeError()
    }
  }

  // don't push an out-of-range value to the preview while the user is
  // still typing - it resizes on every keystroke until the amount is
  // valid; checkError() still validates against inputValue regardless
  if (isInRange(numeric)) emit('update:modelValue', numeric)
}

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  setValue(target.value)
  // stripped characters don't change rawInput, so Vue won't re-render the
  // field - write the sanitized value back ourselves
  if (target.value !== display.value) target.value = display.value
}

// modelValue lags behind the input while a typed amount is out of range
// (typing "500" leaves it at the "50" emitted on the way), so only highlight a
// chip when the input isn't showing a different value. In the hero variant
// any typed amount deselects the chips.
const isChipSelected = (amount: number) =>
  modelValue === amount &&
  (inputValue.value === null || (!isHero.value && inputValue.value === amount))

const chipClass = (amount: number) => {
  const selected = isChipSelected(amount)
  if (isHero.value) {
    return selected
      ? '!border-teal-900 !bg-teal-900 !text-white'
      : 'border-gray-400'
  }
  return selected
    ? 'border-teal-600 !bg-teal-50 text-teal-800 !text-primary'
    : 'border-gray-300'
}

const selectAmount = (amount: number) => {
  error.value.visible = false
  invalid.value = false
  // hero: picking a chip clears the custom field. Otherwise set the input
  // directly - if modelValue already equals this amount the watcher won't
  // fire, and the input would keep the stale typed value
  inputValue.value = isHero.value ? null : amount
  syncRawInput()
  emit('update:modelValue', amount)
}

// radiogroup keyboard pattern: one tab stop (the checked chip, else the
// first), arrows move and select, space/enter select
const chipGroup = ref<HTMLElement | null>(null)
const focusableChipIndex = computed(() => {
  const index = presets.findIndex((amount) => isChipSelected(amount))
  return index === -1 ? 0 : index
})

const handleChipKeydown = (event: KeyboardEvent, index: number) => {
  const step = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1,
  }[event.key]

  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    selectAmount(presets[index]!)
    return
  }
  if (!step) return

  event.preventDefault()
  const next = (index + step + presets.length) % presets.length
  selectAmount(presets[next]!)
  nextTick(() => {
    const chips =
      chipGroup.value?.querySelectorAll<HTMLElement>('[role="radio"]')
    chips?.[next]?.focus()
  })
}

const checkError = (): boolean => {
  // empty custom field just means "no override" - not an invalid amount,
  // unless nothing was chosen at all and an amount is required
  if (inputValue.value === null) {
    const isMissing = required && modelValue === null
    error.value.visible = isMissing
    if (isMissing) error.value.message = t('giftcard.amountRequired')
    return !isMissing
  }

  const isInputValid = isInRange(inputValue.value)

  if (!isInputValid) {
    error.value.visible = true
    error.value.message = rangeError()
  }

  return isInputValid
}

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true
  syncRawInput()

  const target = event.target as HTMLInputElement
  // move the cursor to the end now that the " €" suffix just disappeared
  setTimeout(
    () =>
      target.setSelectionRange(rawInput.value.length, rawInput.value.length),
    0
  )
}

const handleBlur = () => {
  isFocused.value = false
  checkError()
}

defineExpose({ checkError })
</script>
<style scoped lang="postcss">
.giftcard-quantity {
  @apply w-full h-11 max-w-screen-sm md:max-w-[6em] px-4 md:px-0;
}
</style>
